// =============================================================================
// Client-side image processor — resize + re-encode for avatars and icons
// =============================================================================
// Why client-side compression: server-side resize would mean shipping the
// full original photo (could be 5-10 MB from a phone), running ImageMagick
// or similar in the backend, plus storage churn. Doing it in-browser via
// canvas means we ship a small, already-encoded blob.
//
// Strategy:
//   1. Read the file into an Image
//   2. Apply EXIF orientation if present (so portrait phone photos don't
//      end up sideways)
//   3. Compute a target size — preserve aspect ratio, fit inside MAX_DIMENSION
//   4. Square-crop from the center (avatars/icons display in a square box)
//   5. Draw to canvas, export as WebP
//   6. Tweak quality to hit a target byte budget if first attempt is too big
// =============================================================================

interface ProcessOptions {
  maxDimension?: number;  // longest side after resize (default 256)
  targetBytes?: number;   // try to keep output under this (default 80KB)
  format?: 'image/webp' | 'image/jpeg';
}

interface ProcessResult {
  blob: Blob;
  width: number;
  height: number;
  contentType: string;
  sizeBytes: number;
}

const DEFAULT_MAX_DIMENSION = 256;
const DEFAULT_TARGET_BYTES = 80 * 1024;

export async function processImageForAvatar(file: File, opts: ProcessOptions = {}): Promise<ProcessResult> {
  const maxDimension = opts.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const targetBytes = opts.targetBytes ?? DEFAULT_TARGET_BYTES;
  const format = opts.format ?? 'image/webp';

  // Decode the file. createImageBitmap handles EXIF orientation when given
  // imageOrientation: 'from-image' in supporting browsers.
  const bitmap = await createImageBitmap(file, {
    imageOrientation: 'from-image',
  });

  try {
    // Center-crop to a square based on the smaller dimension, then scale to
    // maxDimension. Avatars/icons render square so we crop rather than
    // letterbox to avoid weird empty space.
    const sourceSize = Math.min(bitmap.width, bitmap.height);
    const sourceX = (bitmap.width - sourceSize) / 2;
    const sourceY = (bitmap.height - sourceSize) / 2;
    const targetSize = Math.min(sourceSize, maxDimension);

    const canvas = document.createElement('canvas');
    canvas.width = targetSize;
    canvas.height = targetSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to acquire 2D canvas context');

    // Better quality scaling
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      bitmap,
      sourceX, sourceY, sourceSize, sourceSize,  // source rect (square)
      0, 0, targetSize, targetSize                 // dest rect
    );

    // Try a few quality levels until we hit the target size. WebP at q=0.85
    // is usually plenty; we step down if the image is dense.
    const qualities = [0.85, 0.75, 0.65, 0.5];
    let bestBlob: Blob | null = null;

    for (const q of qualities) {
      const blob = await canvasToBlob(canvas, format, q);
      if (!blob) continue;
      if (blob.size <= targetBytes) {
        return {
          blob,
          width: targetSize,
          height: targetSize,
          contentType: format,
          sizeBytes: blob.size,
        };
      }
      // Track the smallest blob in case we never get under the target
      if (!bestBlob || blob.size < bestBlob.size) {
        bestBlob = blob;
      }
    }

    if (!bestBlob) throw new Error('Failed to encode image');
    return {
      blob: bestBlob,
      width: targetSize,
      height: targetSize,
      contentType: format,
      sizeBytes: bestBlob.size,
    };
  } finally {
    bitmap.close();
  }
}

/**
 * Process a wide banner image — unlike the avatar path this preserves the
 * aspect ratio (no square crop) and just fits the image inside a max box.
 * Used for profile page banners which display as a wide strip.
 */
export async function processImageForBanner(file: File, opts: ProcessOptions = {}): Promise<ProcessResult> {
  const maxDimension = opts.maxDimension ?? 1200;
  const targetBytes = opts.targetBytes ?? 500 * 1024;
  const format = opts.format ?? 'image/webp';

  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    // Scale to fit inside maxDimension on the longest side, never upscaling.
    const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to acquire 2D canvas context');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);

    const qualities = [0.85, 0.75, 0.65, 0.5, 0.4];
    let bestBlob: Blob | null = null;
    for (const q of qualities) {
      const blob = await canvasToBlob(canvas, format, q);
      if (!blob) continue;
      if (blob.size <= targetBytes) {
        return { blob, width, height, contentType: format, sizeBytes: blob.size };
      }
      if (!bestBlob || blob.size < bestBlob.size) bestBlob = blob;
    }
    if (!bestBlob) throw new Error('Failed to encode image');
    return { blob: bestBlob, width, height, contentType: format, sizeBytes: bestBlob.size };
  } finally {
    bitmap.close();
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), type, quality);
  });
}
